import React, { useState, useEffect, useCallback } from "react";
import { Table, Tag, Select, Alert, Input, Pagination } from "antd";
import { Link } from "react-router-dom";
import { useSupplies, mapOrderFromApi } from "../context/SuppliesContext";
import { fetchActiveOrdersAsync } from "../utils/fetch";
import { ExpandedOrderRow } from "./ExpandedOrderRow";

const getPriorityColor = (priority) => {
    if (priority === "Запуск") return "#52c41a";
    if (priority === "Бронь") return "#1677ff";
    if (priority === "Стоп") return "#ff4d4f";
    return "#d9d9d9";
};

const statusOptions = [
    { value: "", label: "—" },
    { value: "Незакуплено", label: "Незакуплено" },
    { value: "Закуплено", label: "Закуплено" },
    { value: "Отгружено", label: "Отгружено" },
];

const getStatusColor = (status) => {
    if (status === "Закуплено") return "green";
    if (status === "Незакуплено") return "red";
    if (status === "Отгружено") return "orange";
    return undefined;
};

const AllApplications = () => {
    const { rawOrders, warehouseStock, updateOrderStatus } = useSupplies();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [updatingIds, setUpdatingIds] = useState(new Set());
    const [searchText, setSearchText] = useState('');
    
    // Пагинация
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchActiveOrdersAsync("ALL", currentPage, pageSize, searchText);
            if (result && result.application) {
                const mapped = result.application.map((app, index) => ({
                    ...mapOrderFromApi(app),
                    num: (currentPage - 1) * pageSize + index + 1,
                }));
                setData(mapped);
                setTotalCount(result.totalCount || 0);
            }
        } catch (e) {
            console.error("Ошибка загрузки AllApplications:", e);
            setError("Не удалось загрузить данные. Пожалуйста, обновите страницу.");
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchText]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1); // сброс на 1 страницу при поиске
    };

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        if (size !== pageSize) {
            setPageSize(size);
            setCurrentPage(1);
        }
    };

    const handleStatusChange = async (orderId, newStatusRu) => {
        const STATUS_MAP_REVERSE = {
            "Незакуплено": "NOT_PURCHASED",
            "Закуплено": "PURCHASED",
            "Отгружено": "SHIPPED",
        };
        const newStatusBackend = STATUS_MAP_REVERSE[newStatusRu] || "NOT_PURCHASED";

        setUpdatingIds((prev) => new Set(prev).add(orderId));
        // Оптимистичное обновление локального стейта
        setData(prev => prev.map(o => o._id === orderId ? { ...o, status: newStatusRu, backendStatus: newStatusBackend } : o));
        
        const success = await updateOrderStatus(orderId, newStatusBackend, newStatusRu);
        
        if (!success) {
            // В случае ошибки перезагружаем данные
            loadData();
        }
        
        setUpdatingIds((prev) => {
            const next = new Set(prev);
            next.delete(orderId);
            return next;
        });
    };

    const columns = [
        {
            title: "Номер договора",
            dataIndex: "contractNum",
            key: "contractNum",
            width: 140,
            render: (text, record) => (
                <Link to={`/client/${record.clientId}`}>{text}</Link>
            ),
        },
        {
            title: "Срок сдачи",
            dataIndex: "deadline",
            key: "deadline",
            width: 100,
            align: "center",
        },
        {
            title: "Приоритет",
            dataIndex: "priority",
            key: "priority",
            width: 90,
            align: "center",
            filters: [
                { text: "Запуск", value: "Запуск" },
                { text: "Бронь", value: "Бронь" },
                { text: "Стоп", value: "Стоп" },
            ],
            onFilter: (value, record) => record.priority === value,
            render: (v) => (
                <span style={{ color: getPriorityColor(v), fontWeight: 600 }}>
                    {v}
                </span>
            ),
        },
        {
            title: "Кол-во дверей",
            dataIndex: "doorCount",
            key: "doorCount",
            width: 100,
            align: "center",
        },
        {
            title: "Алюминий",
            dataIndex: "aluminium",
            key: "aluminium",
            width: 130,
            align: "center",
        },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            width: 160,
            align: "center",
            filters: [
                { text: "Незакуплено", value: "Незакуплено" },
                { text: "Закуплено", value: "Закуплено" },
                { text: "Отгружено", value: "Отгружено" },
                { text: "—", value: "" },
            ],
            onFilter: (value, record) => (record.status || "") === value,
            render: (status, record) => (
                <Select
                    size="small"
                    value={status || ""}
                    options={statusOptions}
                    onChange={(val) => handleStatusChange(record.key, val)}
                    style={{ width: 140 }}
                    loading={updatingIds.has(record.key)}
                    disabled={updatingIds.has(record.key)}
                />
            ),
        },
    ];

    if (error) {
        return (
            <Alert
                type="error"
                message="Ошибка загрузки"
                description={error}
                showIcon
                style={{ margin: 16 }}
            />
        );
    }

    return (
        <div style={{ marginTop: 16 }}>
            <Alert
                message="Инструкция по статусам"
                description="Ставьте «Закуплено» только после физического получения товара на склад. Статус «Отгружено» списывает товар со склада."
                type="info"
                showIcon
                closable
                style={{ marginBottom: 16 }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Input.Search
                    placeholder="Поиск по № договора..."
                    allowClear
                    onChange={handleSearchChange}
                    style={{ width: 300 }}
                />
            </div>

            <div style={{ overflowX: "auto" }}>
                <Table
                    size="small"
                    bordered
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                    locale={{ emptyText: "Заявки не найдены" }}
                    loading={loading}
                    style={{ minWidth: 700 }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <ExpandedOrderRow
                                record={record}
                                rawOrders={rawOrders}
                                warehouseStock={warehouseStock}
                            />
                        ),
                        rowExpandable: () => true,
                    }}
                />
            </div>

            {totalCount > 0 && (
                <div style={{ marginTop: 16, textAlign: "center" }}>
                    <Pagination
                        current={currentPage}
                        total={totalCount}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '50', '100']}
                        onShowSizeChange={handlePageChange}
                        showTotal={(total) => `Всего: ${total} заявок`}
                    />
                </div>
            )}
        </div>
    );
};

export default AllApplications;
